import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { AuthShell, GoogleButton, GOOGLE_AUTH_ENABLED } from "@/components/auth/AuthShell";
import { RestoreAccountDialog } from "@/components/account/RestoreAccountDialog";
import { MfaChallenge } from "@/components/auth/MfaChallenge";
import { RequiredPasswordChange } from "@/components/auth/RequiredPasswordChange";
import { useAuth } from "@/context/AuthContext";
import { sanitizeNext } from "@/lib/safeReturn";

function fmtDate(s) {
  if (!s) return null;
  try { return new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return null; }
}

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null);
  const [mfaToken, setMfaToken] = useState(null);
  const [changeToken, setChangeToken] = useState(null);
  const [expiredMsg, setExpiredMsg] = useState("");

  // OAD-099 — return the member to the exact protected path they were trying to reach.
  const nextPath = sanitizeNext(params.get("next"));
  const landing = (u) => (u.account_complete ? (nextPath || "/dashboard") : "/welcome");

  const deleted = params.get("deleted") === "1";
  const sessionExpired = params.get("session") === "expired";
  const deletedUntil = fmtDate(params.get("purge"));

  React.useEffect(() => {
    if (params.get("error") === "google") toast.error("Google sign-in failed. Please try again.");
    if (params.get("unavailable") === "1") setPending({ restorable: false });
    if (params.get("restore") === "1" && params.get("email")) {
      setPending({ email: params.get("email"), purgeAt: params.get("purge"), restorable: true });
    }
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setExpiredMsg("");
    try {
      const res = await login(form.email, form.password);
      if (res && res.pending_deletion) {
        // self-deleted -> restore dialog; admin-deleted -> unavailable dialog.
        setPending({ email: res.email, purgeAt: res.purge_at, restorable: res.restorable !== false });
        return;
      }
      if (res && res.mfa_required) {
        // Password OK but MFA required — no session yet; show the challenge step.
        setMfaToken(res.mfa_token);
        return;
      }
      if (res && res.password_change_required) {
        // Admin-created temp password OK — no session yet; force the password change.
        setChangeToken(res.change_token);
        return;
      }
      if (res && res.temporary_password_expired) {
        // Temp password lapsed — show a notice, no session issued.
        setExpiredMsg(res.message || "This temporary password has expired. Please contact an administrator or use \u201cForgot password\u201d.");
        setForm((f) => ({ ...f, password: "" }));
        return;
      }
      toast.success(`Welcome back, ${res.preferred_first_name || res.first_name}!`);
      navigate(landing(res));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Log in to your Startup Otter account."
      footer={<>Don't have an account? <Link to="/register" className="font-[560] text-primary hover:underline">Create one</Link></>}
    >
      {deleted && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="login-deleted-banner">
          Your account is pending deletion{deletedUntil ? ` until ${deletedUntil}` : ""}. Sign in before then if you want to restore it.
        </div>
      )}
      {sessionExpired && !deleted && (
        <div className="mb-4 rounded-md border border-border bg-secondary/60 p-3 text-sm text-muted-foreground" data-testid="login-session-expired-banner">
          Your session expired. Please sign in again.
        </div>
      )}
      {expiredMsg && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="login-temp-expired-banner">
          {expiredMsg}
        </div>
      )}
      {changeToken ? (
        <RequiredPasswordChange
          changeToken={changeToken}
          onSuccess={(u) => {
            toast.success(`Welcome, ${u.preferred_first_name || u.first_name}!`);
            navigate(landing(u));
          }}
          onExpired={(msg) => {
            setChangeToken(null);
            setExpiredMsg(msg || "This password-change session has expired. Please sign in again with your temporary password.");
            setForm((f) => ({ ...f, password: "" }));
          }}
        />
      ) : mfaToken ? (
        <MfaChallenge
          mfaToken={mfaToken}
          onSuccess={(u) => {
            toast.success(`Welcome back, ${u.preferred_first_name || u.first_name}!`);
            navigate(landing(u));
          }}
        />
      ) : (
      <>
      {GOOGLE_AUTH_ENABLED && (
        <>
          <GoogleButton label="Log in with Google" />
          <div className="my-5 flex items-center gap-3">
            <Separator className="flex-1" /><span className="text-xs text-muted-foreground">or</span><Separator className="flex-1" />
          </div>
        </>
      )}
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="auth-email-input" />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/reset-password" className="text-xs text-primary hover:underline">Forgot?</Link>
          </div>
          <Input id="password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="auth-password-input" />
        </div>
        <Button type="submit" className="w-full" disabled={loading} data-testid="auth-submit-button">
          {loading ? "Logging in…" : "Log in"}
        </Button>
      </form>
      </>
      )}
      <RestoreAccountDialog
        open={!!pending}
        onOpenChange={(o) => { if (!o) setPending(null); }}
        email={pending?.email}
        purgeAt={pending?.purgeAt}
        restorable={pending?.restorable !== false}
      />
    </AuthShell>
  );
}
